/* eslint-disable import/no-cycle */
import queryString from 'query-string';
import { log, LOG_LEVELS } from '../../common/config/app_logger';
import { getDeviceId } from './device_id';
import { updateLastHit } from './analytics/implementations/mixpanel';
import { setErrorAction, errorTypes } from '../redux/application/application_action';
import {
  browserContext, toCamelCase, toSnakeCase, isVersionHigher, matchPaths
} from '../../common/config/utils';
import { supportedNewPOWalletVersion } from '../../common/config/const';
import { getCurrentLocalization } from './localization';
import { generalPaths } from '../components/routes/routes_const';

const getWebClient = () => {
  let webClient = { WEB_CLIENT: 'POWeb' };
  const pathname = [
    generalPaths.purchaseDetails,
    generalPaths.ESpurchaseDetails,
    generalPaths.paymentSources,
    generalPaths.ESpaymentSources
  ];
  const match = matchPaths({ pathname, path: window.location.pathname });
  if (isVersionHigher(supportedNewPOWalletVersion) && match) {
    webClient = { WEB_CLIENT: 'POMBA' };
  }
  return webClient;
};

const headers = (deviceId) => {
  const deviceHeader = deviceId ? { WEBDEVICE_ID: deviceId } : null;
  return ({
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      'Language-Code': getCurrentLocalization(),
      ...deviceHeader,
      ...getWebClient()
    }
  });
};

const handleOKResponse = (response) => toCamelCase(response);

const handleError = ({ jsonError, resolve }) => {
  const parsedJsonError = toCamelCase(jsonError);
  const {
    operationStatus, error, errors, fullMessages, description, ...rest
  } = parsedJsonError;
  if (operationStatus) return resolve(operationStatus);
  if (error) return resolve({ error, ...rest });
  if (errors) return resolve(errors);
  if (fullMessages) return resolve(fullMessages);
  if (description) return resolve(description);
  return resolve(parsedJsonError);
};

const handleErrorPromise = (e) => new Promise(async (resolve, reject) => {
  e.json()
    .then((jsonError) => { handleError({ jsonError, resolve }); })
    .catch(() => {
      window.store.dispatch(setErrorAction({ [errorTypes.error]:  e.statusText }));
      reject(e);
    });
});

const request = (url, { options = {}, ignoreBaseUrl = false } = {}) => new Promise(async (resolve, reject) => {
  const deviceId = await getDeviceId();
  const baseUrl = ignoreBaseUrl ? url : (window.endpoint || '') + url;
  const apiMethod = options && options.method ? options.method : 'GET';
  updateLastHit();

  window.fetch(baseUrl, {
    ...options,
    ...headers(deviceId),
    credentials: 'include'
  }).then((res) => {
    if (res.ok) {
      if (res.status === 204) {
        resolve(toCamelCase({ success: 'ok' }));
        return;
      }
      res.json().then((responseJSON) => {
        log(`API request ${ apiMethod } ${ url } success with response`, responseJSON);
        log(LOG_LEVELS.DEBUG, 'API', 'Request options', options);
        resolve(handleOKResponse(responseJSON));
      });
      return;
    }
    handleErrorPromise(res).then((error) => {
      log(`API request ${ apiMethod } ${ url } error wit /n`, error);
      log(LOG_LEVELS.DEBUG, 'API', 'Request options', options);
      reject(error);
    }).catch((e) => {
      reject(e);
    });
  }).catch((e) => {
    log(`API request ${ apiMethod } ${ url } error wit /n`, e);
    log(LOG_LEVELS.DEBUG, 'API', 'Request options', options);
    reject(e);
  });
});

export const getTopicsAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getTopicsAPI');
  return request('/purple_topics');
};

export const getAdvisorsAPI = (paginationParams) => {
  let url = '/pending_web_order/advisors';

  if (paginationParams) {
    const queryParams = Object.entries(paginationParams)
      .map(([key, value]) => `${ encodeURIComponent(key) }=${ encodeURIComponent(value) }`)
      .join('&');

    url += `?${ queryParams }`;
  }

  return request(url);
};

export const createPendingWebOrderAPI = ({ token, ...props }) => {
  const params = JSON.stringify(toSnakeCase({ pendingWebOrder: props, rc_token: token }));
  log(LOG_LEVELS.INFO, 'API', 'createPendingWebOrdersAPI', { parameters: params });
  return request('/pending_web_order', {
    options: {
      method: 'POST',
      body: params
    }
  });
};

export const editPendingWebOrderAPI = (props) => {
  const params = JSON.stringify(toSnakeCase({ pendingWebOrder: props }));
  log(LOG_LEVELS.INFO, 'API', 'editPendingWebOrderAPI', { parameters: params });
  return request('/pending_web_order', {
    options: {
      method: 'PUT',
      body: params
    }
  });
};

export const pendingWebOrderSubmitPaymentSourceAPI = ({ paymentSourceId, params }) => {
  const parameters = JSON.stringify(params);
  log(LOG_LEVELS.INFO, 'API', 'pendingWebOrderSubmitApi', { parameters });
  return request(`/pending_web_order/submit_payment_source/${ paymentSourceId }`, {
    options: {
      method: 'POST',
      body: parameters
    }
  });
};

export const createCreditCardProcessingPurchaseApi = (params) => {
  const parameters = JSON.stringify(toSnakeCase({ ...params }));
  log(LOG_LEVELS.INFO, 'API', 'createCreditCardProcessingPurchase', { parameters });
  return request('/my/user_credits', {
    options: {
      method: 'POST',
      body: parameters
    }
  });
};

export const getCreditCardProcessingPurchaseResponceApi = (id) => {
  log(LOG_LEVELS.INFO, 'API', 'getCreditCardProcessingPurchaseResponce', { id });
  return request(`/my/user_credits/${ id }`);
};

export const pendingWebOrderSubmitAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'pendingWebOrderSubmitAPI');
  return request('/pending_web_order/submit', { options: { method: 'POST' } });
};

export const logOutAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'logOutAPI');
  return request('/users/logout/', { options: { method: 'DELETE' } });
};

export const getUserAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getCurrentUserAPI');
  const query = queryString.stringify({ with_verification: true });
  return request(`/my/account?${ query }`);
};

export const updateUserAPI = (payload) => {
  log(LOG_LEVELS.INFO, 'API', 'updateUserAPI');
  return request('/my/account/', {
    options: {
      method: 'PUT',
      body: JSON.stringify(toSnakeCase({ user:{ ...payload } }))
    }
  });
};

export const getPaymentConfigsAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'getPaymentConfigsAPI');
  return request('/pending_web_order/payment_configs', {
    options: {
      method: 'POST',
      body: JSON.stringify({ rc_token: token })
    }
  });
};

export const validateEmailAPI = (email) => {
  log(LOG_LEVELS.INFO, 'API', 'pendingWebOrderValidateEmail');
  const query = queryString.stringify({ email });
  return request(`/pending_web_order/validate_params?${ query }`);
};

export const validateNicknameAPI = (nickname) => {
  log(LOG_LEVELS.INFO, 'API', 'validateNicknameAPI');
  const query = queryString.stringify({ nickname });
  return request(`/pending_web_order/validate_params?${ query }`);
};

export const loadOrderAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'loadOrderAPI');
  return request(`/pending_web_order/${ token }`);
};

export const onOrderSeenAPI = (token) => {
  log(LOG_LEVELS.INFO, 'API', 'onOrderSeen');
  return request(`/pending_web_order/${ token }/seen`, { options: { method: 'PUT' } });
};

export const getWebOrderInfoAPI = () => {
  log(LOG_LEVELS.INFO, 'API', 'getwebOrderInfoAPI');
  return request('/pending_web_order/web_order_data');
};

export const deletePaymentSourceAPI = (id) => {
  log(LOG_LEVELS.INFO, 'API', 'deletePaymentSourceAPI');
  return request(`/my/payment_sources/${ id }`, { options: { method: 'DELETE' } });
};

export const loadAvailablePaymentSourcesApi = (isMyAccountPage = null) => {
  log(LOG_LEVELS.INFO, 'API', 'loadAvailablePaymentSourcesApi');
  let params = { ...browserContext };
  if (isMyAccountPage) {
    params = { ...params, is_my_account_page: isMyAccountPage };
  }
  const queryParameters = queryString.stringify(params);
  return request(`/my/payment_sources?${ queryParameters }`);
};

export const addPaymentSourceApi = (method) => {
  log(LOG_LEVELS.INFO, 'API', 'addPaymentSourceApi');
  return request('/my/payment_sources', {
    options: {
      method: 'POST',
      body: JSON.stringify({ method, ...browserContext })
    }
  });
};

export const editPaymentSourceApi = ({ id, params, type }) => {
  const parameters = JSON.stringify({ ...params, ...browserContext });
  log(LOG_LEVELS.INFO, 'API', { type }, { parameters });
  return request(`/my/payment_sources/${ id }`, {
    options: {
      method: 'PUT',
      body: parameters
    }
  });
};

export const removePaymentSourceApi = (id) => {
  log(LOG_LEVELS.INFO, 'API', 'removePaymentSourceApi');
  return request(`/my/payment_sources/${ id }`, { options: { method: 'DELETE' } });
};

export const loadPaymentOptionsApi = (token, isMyAccountPage = null) => {
  log(LOG_LEVELS.INFO, 'API', 'loadPaymentOptionsApi');
  let params = { ...browserContext, rc_token: token };
  if (isMyAccountPage) {
    params = { ...params, is_my_account_page: isMyAccountPage };
  }
  return request('/my/payment_sources/current', {
    options: {
      method: 'POST',
      body: JSON.stringify(params)
    }
  });
};

export const getFeesApi = ({ amountToPay }) => {
  log(LOG_LEVELS.INFO, 'API', `getFees for ${ amountToPay }`);
  const query = queryString.stringify({ amount_to_pay: amountToPay });
  return request(`/my/app_credits/calculate_fees?${ query }`);
};

export const getCreditCardProcessingDataApi = ({ url, params }) => {
  const queryParameters = queryString.stringify({ ...params });
  log(LOG_LEVELS.INFO, 'API', 'getCreditCardProcessingDataApi', { parameters: queryParameters });
  return request(`${ url }${ queryParameters }`, { ignoreBaseUrl: true });
};

export const getAdditionalPurchaseInfoFromURL = (URL) => {
  log(LOG_LEVELS.INFO, 'API', `getAdditionalPurchaseInfoFromURL ${ URL }`);
  return request(URL);
};
